/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Card, CardContent, CardExtra } from '../../../Shared/Card'
import { Icon, Statistic, StatisticLabel, StatisticValue, Progress } from 'semantic-ui-react'
import { Link } from 'react-router-dom-v5-compat'
import { useQuery } from '@tanstack/react-query'
import { getMetricsToday } from '../../../../../services/Metrics/Metrics.service'
import { formatISO, startOfDay, roundToNearestMinutes, format } from 'date-fns'
import { enUS } from 'date-fns/locale'
import useUser from '../../../../Shared/UserProvider/useUser'
import { isProviderAPos } from '../../../../../services/Provider/Provider.service'
import { getCancellationSource } from '../../../../../services/http-client'
import { intlFormatMoneyWithCompact, ONE_THOUSAND } from '../../../../../helpers/money'
import { NoTodayPositiveActivityCardContent } from './NoTodayPositiveActivityCardContent'
import { LoadingPlaceholderCardContent } from '../../../Shared/LoadingPlaceholderCardContent'
import { useTranslation } from 'react-i18next'
import { CardTitle } from '../../../Shared/CardTitle'
import React from 'react'
import { intlFormatNumberWithCompact, TEN_THOUSAND } from '../../../../../helpers/number'

const TodayPositiveActivityCard = () => {
    const [t] = useTranslation()
    const {user} = useUser()

    const now = new Date()
    const from = formatISO(startOfDay(now))
    const to = formatISO(roundToNearestMinutes(now, {nearestTo: 10, roundingMethod: 'floor'}))

    const {data: todayMetrics, isLoading} = useQuery({
        queryKey: ['today-positive-metrics', from, to],
        queryFn: async () => getMetricsToday({
            dateRange: {
                startDate: from,
                endDate: to,
            },
            merchantId: isProviderAPos(user.provider) ? user.provider.object.id : null,
            brandIds: [],
            platformIds: [],
        }, getCancellationSource()),
    })

    const todayOrders = (todayMetrics?.todayArray || []).filter(metric => metric.dt === 'TODAY')
    const todayOrdersCount = todayOrders.reduce((total, metric) => total + (+metric.nborder || 0), 0)
    const todayOrdersRevenues = todayOrders.reduce((total, metric) => total + (+metric.revenues || 0), 0)

    const currentHour = now.getHours()
    const pastOrders = (todayMetrics?.todayArray || []).filter(metric => metric.dt === 'PAST' && +metric.hh <= currentHour)
    const pastOrdersRevenues = Math.round(pastOrders.reduce((total, metric) => total + (Math.floor(+metric.revenues) || 0), 0))
    const dayName = format(now, 'EEEE', {locale: enUS}).toLowerCase()

    const revenueProgress = pastOrdersRevenues > 0
      ? (todayOrdersRevenues / pastOrdersRevenues) * 100
      : (todayOrdersRevenues > 0 ? 100 : 0)

    return (
      <Card
        css={css`
            border-top: 2px solid ${isLoading ? 'unset' : '#21ba45'};

            .card-content {
                width: 360px;
                height: 402px;
            }
        `}
      >
          {isLoading ? (
            <LoadingPlaceholderCardContent/>
          ) : (
            (todayOrders && todayOrders.length > 0) ? <CardContent
                css={css`
                    display: grid;
                    place-items: center;
                    gap: 3rem;
                    grid-template-areas: ${isLoading ? `'a'` : `'title title' 'a a' 'b b' 'progress progress'`};
                    grid-template-rows: 30px repeat(3, 1fr);
                `}
              >
                  <>
                      <CardTitle
                        title={t('dashboard.today_positive_activity.today')}
                      />

                      <Statistic
                        css={css`
                            grid-area: a;

                            &.ui.statistic, &.ui.statistic + .ui.statistic {
                                margin: 0;
                            }
                        `}
                      >
                          <StatisticValue>
                              {intlFormatMoneyWithCompact(todayOrdersRevenues, user.getCompanyCurrency(), user.getCompanyLocale(), ONE_THOUSAND)}
                          </StatisticValue>
                          <StatisticLabel>
                              {t('home.revenues')}
                          </StatisticLabel>
                      </Statistic>

                      <Statistic
                        css={css`
                            grid-area: b;

                            &.ui.statistic + .ui.statistic {
                                margin: 0;
                            }
                        `}
                        size="small"
                      >
                          <StatisticValue>
                              {intlFormatNumberWithCompact(todayOrdersCount, user.getCompanyLocale(), TEN_THOUSAND)}
                          </StatisticValue>
                          <StatisticLabel>
                              {t('home.number_order', {count: todayOrdersCount})}
                          </StatisticLabel>
                      </Statistic>

                      <div
                        css={css`
                            grid-area: progress;
                            align-self: end;
                            width: 100%;
                            padding: 1rem;
                        `}
                      >
                          {revenueProgress > 100 && (
                            <div
                              css={css`
                                  text-align: center;
                                  font-size: 1.2rem;
                                  font-weight: 700;
                                  color: #21ba45;
                                  margin-bottom: 0.5rem;
                                  display: flex;
                                  align-items: center;
                                  justify-content: center;
                                  gap: 0.2rem;
                              `}
                            >
                                <Icon name="chart line"/>
                                +{Math.round(revenueProgress - 100)}%
                            </div>
                          )}
                          <Progress
                            percent={Math.round(revenueProgress)}
                            color="green"
                            size="medium"
                            progress={revenueProgress <= 100}
                            css={css`
                                &.ui.progress {
                                    margin-top: 0;
                                    margin-bottom: 0;
                                }
                            `}
                          />
                          <div
                            css={css`
                                text-align: center;
                                font-size: 0.75rem;
                                color: rgba(0, 0, 0, 0.6);
                            `}
                          >
                              {t('dashboard.today_positive_activity.progress_label', {
                                  dayOfWeek: t(`days.${dayName}`, {count: 4}).toLowerCase(),
                                  value: pastOrdersRevenues > 0 ? intlFormatMoneyWithCompact(pastOrdersRevenues, user.getCompanyCurrency(), user.getCompanyLocale(), ONE_THOUSAND) : '-',
                              })}
                          </div>
                      </div>
                  </>
              </CardContent> :
              <NoTodayPositiveActivityCardContent/>
          )}

          <CardExtra>
              <Link
                to={{
                    pathname: '/analytics',
                    search: new URLSearchParams({
                        from,
                        to,
                    }).toString(),
                }}>
                  {t('home.revenues')}
                  <Icon name="arrow right"/>
              </Link>
          </CardExtra>
      </Card>
    )
}

export { TodayPositiveActivityCard }
